import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Result, Card, Typography, Modal, Input, Button, Form, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import recruitApi from '../../../../api/recruitApi';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

const StyledCard = styled(Card)`
  max-width: 600px;
  margin: 100px auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const StyledResult = styled(Result)`
  padding: 24px 0;
`;

const StyledModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .anticon {
    color: #faad14;
    font-size: 24px;
  }
`;

const StyledModalContent = styled.div`
  margin-top: 16px;
`;

const StyledModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  gap: 8px;
`;

const ApproveRejectOfferCandidate: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [form] = Form.useForm();

  // States
  const [isModalVisible, setIsModalVisible] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get parameters from URL
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const status = searchParams.get('status');

  // Determine if it's an approval or rejection based on status in URL
  const isRejection = status?.toUpperCase() === 'REJECTED';

  const callApi = async (reason = '') => {
    setIsSubmitting(true);
    try {
      if (!token) {
        setError(t('Invalid or missing token. Please use the link from your email.'));
        return;
      }

      // Make API call to submit the response with token and reason
      const response = await recruitApi.offerCandidateApproveOrRejectByToken({
        token,
        reason,
      });

      if (response.data && response.data.success) {
        setIsSuccess(true);
      } else {
        setError(t('Failed to submit your response. Please try again.'));
      }
    } catch (err) {
      console.error('Error submitting response:', err);
      setError(t('An error occurred while submitting your response. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Close modal without submitting
    setIsModalVisible(false);
  };

  const handleSubmit = async () => {
    try {
      // Get form values without validation (reason is optional)
      const values = form.getFieldsValue();
      const reason = values.reason || '';
      await callApi(reason);
      setIsModalVisible(false); // Hide modal after API call
    } catch (err) {
      console.error('Error getting form values:', err);
    }
  };

  const modalTitle = isRejection ? t('Reason for rejection') : t('Reason for acceptance');

  return (
    <>
      <Modal
        title={
          <StyledModalTitle>
            <ExclamationCircleOutlined />
            {modalTitle}
          </StyledModalTitle>
        }
        open={isModalVisible}
        footer={null}
        closable={false}
        maskClosable={false}
        width={600}
      >
        {error && (
          <div style={{ marginBottom: 16 }}>
            <Text type="danger">{error}</Text>
          </div>
        )}

        <StyledModalContent>
          <Form form={form} layout="vertical">
            <Form.Item name="reason">
              <TextArea rows={4} placeholder={t('Please enter a reason (optional)')} />
            </Form.Item>
          </Form>

          {isRejection && (
            <Text type="danger">{t('Your job application will be rejected if you continue!')}</Text>
          )}
        </StyledModalContent>

        <StyledModalFooter>
          <Button onClick={handleCancel}>{t('Cancel')}</Button>
          <Button type="primary" onClick={handleSubmit} loading={isSubmitting} danger={isRejection}>
            {isRejection ? t('Reject') : t('Accept')}
          </Button>
        </StyledModalFooter>
      </Modal>

      {isSuccess && (
        <StyledCard>
          <StyledResult
            status="success"
            title={t('Success')}
            subTitle={t('Your response has been recorded')}
          />
          <div style={{ textAlign: 'center', padding: '0 24px 24px' }}>
            <Paragraph>{t('Thank you for your response.')}</Paragraph>
          </div>
        </StyledCard>
      )}

      {!isSuccess && !isModalVisible && (
        <StyledCard>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
            <Spin size="large" />
          </div>
        </StyledCard>
      )}
    </>
  );
};

export default ApproveRejectOfferCandidate;
